import { type ContextMenuItem, IconButton, MenuOption, usePopoverPosition } from "@design";
import { MoreHorizontal } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

// The design system's ContextMenu is right-click only; list rows need a
// click-triggered overflow menu. Built from the same popover positioning +
// MenuOption so it looks identical. Candidate to move into anthm-design as a
// proper trigger-based Menu (with full listbox keyboard nav) later.
export function RowMenu({ name, items }: Readonly<{ name: string; items: ContextMenuItem[] }>) {
  const { t } = useTranslation("build");
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const { panelRef, style } = usePopoverPosition<HTMLDivElement>({ isOpen: open, anchorRef });

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!anchorRef.current?.contains(e.target as Node) && !panelRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, panelRef]);

  return (
    <div ref={anchorRef} className="relative">
      <IconButton
        icon={<MoreHorizontal />}
        aria-label={t("list.rowActions", { name })}
        aria-expanded={open}
        variant="ghost"
        size="sm"
        onClick={() => setOpen((v) => !v)}
      />
      {open && (
        <div
          ref={panelRef}
          role="menu"
          style={style}
          aria-label={t("list.rowActions", { name })}
          className="z-[110] min-w-40 rounded-xl border border-border-input bg-surface-elevated px-1 py-1 shadow-lg"
        >
          {items.map((item) => (
            <MenuOption
              key={item.label}
              icon={item.icon}
              danger={item.danger}
              disabled={item.disabled}
              onSelect={() => {
                setOpen(false);
                item.onSelect?.();
              }}
            >
              {item.label}
            </MenuOption>
          ))}
        </div>
      )}
    </div>
  );
}
